import { FC, useState, useCallback, useEffect } from 'react';
import {
  RefreshCw,
  Download,
  Eye,
  Shield,
  ShieldAlert,
  CheckCircle,
  XCircle,
  FileText,
  FileJson,
  ChevronDown,
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { SearchInput } from '../../components/admin/SearchInput';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { FormModal } from '../../components/admin/FormModal';
import { Badge } from '../../components/ui/Badge';
import { adminApi } from '../../api/adminApi';
import type {
  AuditLogItem,
  AuditLogDetails,
  AuditLogsListQuery,
  Pagination,
  ActionCategory,
} from '../../types/admin.types';

type PageState = 'loading' | 'loaded' | 'error';

// Helper functions
function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatFullDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

// Action category styling
const categoryConfig: Record<
  ActionCategory,
  { label: string; variant: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' }
> = {
  AUTH: { label: 'Auth', variant: 'info' },
  THERAPIST: { label: 'Therapist', variant: 'primary' },
  SERVICE: { label: 'Service', variant: 'secondary' },
  BOOKING: { label: 'Booking', variant: 'success' },
  PAYMENT: { label: 'Payment', variant: 'warning' },
  USER: { label: 'User', variant: 'info' },
  SETTINGS: { label: 'Settings', variant: 'secondary' },
  REPORT: { label: 'Report', variant: 'primary' },
  OTHER: { label: 'Other', variant: 'info' },
};

// Common actions for filter dropdown
const commonActions = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'APPROVE',
  'REJECT',
  'SUSPEND',
  'REACTIVATE',
  'LOGIN',
  'LOGOUT',
  'VIEW',
  'EXPORT',
];

export const AuditLogsPage: FC = () => {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [query, setQuery] = useState<AuditLogsListQuery>({
    page: 1,
    limit: 20,
  });
  const [errorMessage, setErrorMessage] = useState('');

  // Modal state
  const [selectedLog, setSelectedLog] = useState<AuditLogDetails | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Export dropdown state
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch audit logs
  const fetchLogs = useCallback(async () => {
    try {
      setPageState('loading');
      const response = await adminApi.listAuditLogs(query);
      setLogs(response.data);
      setPagination(response.pagination);
      setPageState('loaded');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load audit logs');
      setPageState('error');
    }
  }, [query]);

  // Fetch logs when query changes
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Handler functions
  const handleSearchChange = useCallback((search: string) => {
    setQuery((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setQuery((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((limit: number) => {
    setQuery((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  const handleFilterChange = useCallback(
    (key: keyof AuditLogsListQuery, value: string) => {
      setQuery((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
    },
    []
  );

  const handleDateFilterChange = useCallback(
    (key: 'startDate' | 'endDate', value: string) => {
      setQuery((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setQuery({ page: 1, limit: 20 });
  }, []);

  const handleViewDetails = useCallback(async (log: AuditLogItem) => {
    setIsLoadingDetails(true);
    setIsModalOpen(true);
    try {
      const details = await adminApi.getAuditLog(log.id);
      setSelectedLog(details);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load log details');
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedLog(null);
  }, []);

  const handleExport = useCallback(
    async (format: 'CSV' | 'JSON') => {
      setIsExporting(true);
      setIsExportOpen(false);
      try {
        // Default to last 30 days if no date range specified
        const today = new Date().toISOString().split('T')[0] as string;
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0] as string;
        const endDate = query.endDate || today;
        const startDate = query.startDate || thirtyDaysAgo;

        const exportQuery: { startDate: string; endDate: string; format: 'CSV' | 'JSON'; adminId?: string; action?: string } = {
          startDate,
          endDate,
          format,
        };
        if (query.adminId) exportQuery.adminId = query.adminId;
        if (query.action) exportQuery.action = query.action;

        const blob = await adminApi.exportAuditLogs(exportQuery);

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-logs-${startDate}-to-${endDate}.${format.toLowerCase()}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to export audit logs');
      } finally {
        setIsExporting(false);
      }
    },
    [query]
  );

  // Check if any filters are active
  const hasActiveFilters =
    query.search ||
    query.action ||
    query.success ||
    query.startDate ||
    query.endDate;

  // Table columns
  const columns: Column<AuditLogItem>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      render: (item) => (
        <span className="text-sm text-gray-700 dark:text-slate-300">
          {formatDateTime(item.timestamp)}
        </span>
      ),
    },
    {
      key: 'adminName',
      header: 'Admin',
      render: (item) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-slate-100">
            {item.adminName || 'Unknown'}
          </p>
          <p className="text-xs text-gray-500 dark:text-slate-400">{item.adminEmail}</p>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (item) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium text-gray-900 dark:text-slate-100">{item.action}</span>
          <Badge variant={categoryConfig[item.actionCategory]?.variant || 'info'} size="sm">
            {categoryConfig[item.actionCategory]?.label || item.actionCategory}
          </Badge>
        </div>
      ),
    },
    {
      key: 'resource',
      header: 'Resource',
      render: (item) => (
        <div>
          <p className="text-gray-900 dark:text-slate-100">{item.resource}</p>
          {item.resourceId && (
            <p className="text-xs text-gray-500 dark:text-slate-400 font-mono">
              {item.resourceId.substring(0, 8)}...
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'ipAddress',
      header: 'IP Address',
      render: (item) => (
        <span className="text-sm font-mono text-gray-600 dark:text-slate-400">
          {item.ipAddress}
        </span>
      ),
    },
    {
      key: 'success',
      header: 'Status',
      render: (item) => (
        <div className="flex items-center gap-2">
          {item.success ? (
            <Badge variant="success" size="sm">
              <CheckCircle className="h-3 w-3 mr-1" />
              Success
            </Badge>
          ) : (
            <Badge variant="error" size="sm">
              <XCircle className="h-3 w-3 mr-1" />
              Failed
            </Badge>
          )}
          {item.isSecurityCritical && (
            <ShieldAlert className="h-4 w-4 text-red-500" aria-label="Security Critical" />
          )}
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Audit Logs
            </h1>
            <p className="mt-1 text-gray-500 dark:text-slate-400">
              View and export admin activity logs
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchLogs()}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            {/* Export Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsExportOpen(!isExportOpen)}
                disabled={isExporting}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {isExporting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Export
                    <ChevronDown className="h-4 w-4" />
                  </>
                )}
              </button>
              {isExportOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg z-10">
                  <button
                    onClick={() => handleExport('CSV')}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-t-lg"
                  >
                    <FileText className="h-4 w-4" />
                    Export as CSV
                  </button>
                  <button
                    onClick={() => handleExport('JSON')}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-b-lg"
                  >
                    <FileJson className="h-4 w-4" />
                    Export as JSON
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && pageState === 'loaded' && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4">
            <p className="text-red-800 dark:text-red-200">{errorMessage}</p>
            <button
              onClick={() => setErrorMessage('')}
              className="mt-1 text-sm text-red-600 dark:text-red-400 hover:text-red-800"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <SearchInput
            value={query.search || ''}
            onChange={handleSearchChange}
            placeholder="Search by action, resource, or message..."
            className="w-64"
          />
          <select
            value={query.action || ''}
            onChange={(e) => handleFilterChange('action', e.target.value)}
            className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 pl-3 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Actions</option>
            {commonActions.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </select>
          <select
            value={query.success || ''}
            onChange={(e) => handleFilterChange('success', e.target.value)}
            className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 pl-3 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-slate-400">From:</label>
            <input
              type="date"
              value={query.startDate || ''}
              onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
              className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-slate-400">To:</label>
            <input
              type="date"
              value={query.endDate || ''}
              onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
              className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={query.limit || 20}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              aria-label="per page"
              className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 pl-3 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={logs}
          keyExtractor={(item) => item.id}
          isLoading={pageState === 'loading'}
          emptyMessage="No audit logs found"
          pagination={pagination || undefined}
          onPageChange={handlePageChange}
          onRowClick={handleViewDetails}
          actions={(item) => (
            <button
              onClick={() => handleViewDetails(item)}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-700"
              title="View details"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
        />

        {/* Details Modal */}
        {isModalOpen && (
          <FormModal
            isOpen
            onClose={handleCloseModal}
            onSubmit={handleCloseModal}
            title="Audit Log Details"
            submitText="Close"
            cancelText=""
            size="lg"
          >
            {isLoadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            ) : selectedLog ? (
              <div className="space-y-6">
                {/* Status and Security Badge */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {selectedLog.success ? (
                      <Badge variant="success">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Success
                      </Badge>
                    ) : (
                      <Badge variant="error">
                        <XCircle className="h-4 w-4 mr-1" />
                        Failed
                      </Badge>
                    )}
                    {selectedLog.isSecurityCritical && (
                      <Badge variant="error">
                        <ShieldAlert className="h-4 w-4 mr-1" />
                        Security Critical
                      </Badge>
                    )}
                  </div>
                  <Badge variant={categoryConfig[selectedLog.actionCategory]?.variant || 'info'}>
                    {categoryConfig[selectedLog.actionCategory]?.label || selectedLog.actionCategory}
                  </Badge>
                </div>

                {/* Message */}
                <div className="rounded-lg bg-gray-50 dark:bg-slate-700 p-4">
                  <p className="text-lg font-medium text-gray-900 dark:text-slate-100">
                    {selectedLog.message}
                  </p>
                </div>

                {/* Admin Info */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Admin</p>
                    <p className="text-gray-900 dark:text-slate-100">
                      {selectedLog.adminName || 'Unknown'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      {selectedLog.adminEmail}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      Timestamp
                    </p>
                    <p className="text-gray-900 dark:text-slate-100">
                      {formatFullDateTime(selectedLog.timestamp)}
                    </p>
                  </div>
                </div>

                {/* Request Info */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Action</p>
                    <p className="text-gray-900 dark:text-slate-100">{selectedLog.action}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      Resource
                    </p>
                    <p className="text-gray-900 dark:text-slate-100">{selectedLog.resource}</p>
                    {selectedLog.resourceId && (
                      <p className="text-sm font-mono text-gray-500 dark:text-slate-400">
                        {selectedLog.resourceId}
                      </p>
                    )}
                  </div>
                </div>

                {/* Technical Details */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      Endpoint
                    </p>
                    <p className="text-sm font-mono text-gray-900 dark:text-slate-100">
                      {selectedLog.method} {selectedLog.endpoint}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      IP Address
                    </p>
                    <p className="text-sm font-mono text-gray-900 dark:text-slate-100">
                      {selectedLog.ipAddress}
                    </p>
                  </div>
                </div>

                {/* User Agent */}
                {selectedLog.userAgent && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      User Agent
                    </p>
                    <p className="text-xs font-mono text-gray-600 dark:text-slate-400 break-all">
                      {selectedLog.userAgent}
                    </p>
                  </div>
                )}

                {/* Error Message */}
                {selectedLog.errorMessage && (
                  <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      Error Message
                    </p>
                    <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                      {selectedLog.errorMessage}
                    </p>
                  </div>
                )}

                {/* Changes */}
                {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                      Changes
                    </p>
                    <pre className="rounded-lg bg-gray-100 dark:bg-slate-800 p-4 text-xs overflow-x-auto">
                      {JSON.stringify(selectedLog.changes, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Metadata */}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">
                      Additional Metadata
                    </p>
                    <pre className="rounded-lg bg-gray-100 dark:bg-slate-800 p-4 text-xs overflow-x-auto">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-12 text-center text-gray-500">No details available</div>
            )}
          </FormModal>
        )}
      </div>
    </AdminLayout>
  );
};

export default AuditLogsPage;
